import React from 'react';
import { Alert, Button, Card, DatePicker, Input, Modal, Select, Space, Statistic, Table, Tag, Tooltip, Typography } from 'antd';

import { platformOperationalAlertsService } from '../../services/api';
import { usePlatformActionPermissions } from '../../hooks/usePlatformActionPermissions';

const { Title, Text } = Typography;

const STATUS_OPTIONS = [
  { label: 'OPEN', value: 'OPEN' },
  { label: 'ACKNOWLEDGED', value: 'ACKNOWLEDGED' },
  { label: 'RESOLVED', value: 'RESOLVED' },
];

const SEVERITY_OPTIONS = [
  { label: 'INFO', value: 'INFO' },
  { label: 'WARNING', value: 'WARNING' },
  { label: 'ERROR', value: 'ERROR' },
  { label: 'CRITICAL', value: 'CRITICAL' },
];

const statusColor = {
  OPEN: 'red',
  ACKNOWLEDGED: 'gold',
  RESOLVED: 'green',
};

const severityColor = {
  INFO: 'blue',
  WARNING: 'orange',
  ERROR: 'red',
  CRITICAL: 'magenta',
};

const getErrorMessage = (requestError, fallback) => {
  const detail = requestError?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) {
    return detail;
  }
  return fallback;
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';
  return parsed.toLocaleString('pt-BR');
};

const PERMISSION_TOOLTIP_MESSAGE = 'Você não possui permissão para executar esta ação';

const withPermissionTooltip = (node, disabledByPermission) => {
  if (!disabledByPermission) {
    return node;
  }

  return (
    <Tooltip title={PERMISSION_TOOLTIP_MESSAGE}>
      <span style={{ display: 'inline-block' }}>{node}</span>
    </Tooltip>
  );
};

const PlatformAlertCenterPage = () => {
  const { canExecuteAction } = usePlatformActionPermissions();
  const [filters, setFilters] = React.useState({
    tenant_id: '',
    status: undefined,
    severity: undefined,
    alert_code: '',
    source: '',
    range: null,
    limit: 200,
  });
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [evaluating, setEvaluating] = React.useState(false);
  const [error, setError] = React.useState('');
  const [actionModal, setActionModal] = React.useState({ open: false, mode: null, alert: null, notes: '' });
  const [actionLoading, setActionLoading] = React.useState(false);

  const fetchAlerts = React.useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const response = await platformOperationalAlertsService.list({
        tenant_id: (filters.tenant_id || '').trim() || undefined,
        status: filters.status || undefined,
        severity: filters.severity || undefined,
        alert_code: (filters.alert_code || '').trim() || undefined,
        source: (filters.source || '').trim() || undefined,
        from_date: filters.range?.[0] ? filters.range[0].startOf('day').toISOString() : undefined,
        to_date: filters.range?.[1] ? filters.range[1].endOf('day').toISOString() : undefined,
        limit: Number(filters.limit) || 200,
      });
      setItems(Array.isArray(response?.data) ? response.data : []);
    } catch (requestError) {
      setItems([]);
      setError(getErrorMessage(requestError, 'Nao foi possivel consultar alertas operacionais.'));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  React.useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  const evaluateNow = async () => {
    setEvaluating(true);
    setError('');

    try {
      await platformOperationalAlertsService.evaluate({
        tenant_id: (filters.tenant_id || '').trim() || undefined,
        from_date: filters.range?.[0] ? filters.range[0].startOf('day').toISOString() : undefined,
        to_date: filters.range?.[1] ? filters.range[1].endOf('day').toISOString() : undefined,
      });
      await fetchAlerts();
    } catch (requestError) {
      setError(getErrorMessage(requestError, 'Falha ao avaliar regras de alerta.'));
    } finally {
      setEvaluating(false);
    }
  };

  const statusCounts = React.useMemo(() => {
    return items.reduce(
      (acc, item) => {
        const status = item?.status || 'OPEN';
        if (status in acc) {
          acc[status] += 1;
        }
        return acc;
      },
      { OPEN: 0, ACKNOWLEDGED: 0, RESOLVED: 0 }
    );
  }, [items]);

  const severityCounts = React.useMemo(() => {
    return items.reduce(
      (acc, item) => {
        const severity = item?.severity || 'INFO';
        if (severity in acc) {
          acc[severity] += 1;
        }
        return acc;
      },
      { INFO: 0, WARNING: 0, ERROR: 0, CRITICAL: 0 }
    );
  }, [items]);

  const openActionModal = (mode, alert) => {
    setActionModal({ open: true, mode, alert, notes: '' });
  };

  const closeActionModal = () => {
    setActionModal({ open: false, mode: null, alert: null, notes: '' });
  };

  const submitAction = async () => {
    if (!actionModal.alert?.id || !actionModal.mode) return;

    setActionLoading(true);
    setError('');

    try {
      if (actionModal.mode === 'ACKNOWLEDGE') {
        await platformOperationalAlertsService.acknowledge(actionModal.alert.id, {
          internal_notes: actionModal.notes || undefined,
        });
      } else {
        await platformOperationalAlertsService.resolve(actionModal.alert.id, {
          internal_notes: actionModal.notes || undefined,
        });
      }
      closeActionModal();
      await fetchAlerts();
    } catch (requestError) {
      setError(getErrorMessage(requestError, 'Falha ao atualizar status do alerta.'));
    } finally {
      setActionLoading(false);
    }
  };

  const columns = [
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 150,
      render: (value) => <Tag color={statusColor[value] || 'default'}>{value || '-'}</Tag>,
    },
    {
      title: 'Severity',
      dataIndex: 'severity',
      key: 'severity',
      width: 130,
      render: (value) => <Tag color={severityColor[value] || 'default'}>{value || '-'}</Tag>,
    },
    {
      title: 'Codigo',
      dataIndex: 'alert_code',
      key: 'alert_code',
      width: 250,
      render: (value) => <Tag>{value || '-'}</Tag>,
    },
    {
      title: 'Titulo',
      dataIndex: 'title',
      key: 'title',
      render: (value) => value || '-',
    },
    {
      title: 'Tenant',
      dataIndex: 'tenant_id',
      key: 'tenant_id',
      width: 220,
      render: (value) => value || '-',
    },
    {
      title: 'Last Seen',
      dataIndex: 'last_seen_at',
      key: 'last_seen_at',
      width: 180,
      render: (value) => formatDateTime(value),
    },
    {
      title: 'Acoes',
      key: 'actions',
      width: 220,
      render: (_, row) => {
        const canAcknowledge = canExecuteAction('alerts.acknowledge');
        const canResolve = canExecuteAction('alerts.resolve');
        const isResolved = row.status === 'RESOLVED';

        return (
          <Space>
            {withPermissionTooltip(
              <Button
                size="small"
                onClick={() => openActionModal('ACKNOWLEDGE', row)}
                disabled={isResolved || !canAcknowledge}
              >
                Reconhecer
              </Button>,
              !canAcknowledge
            )}
            {withPermissionTooltip(
              <Button
                size="small"
                type="primary"
                onClick={() => openActionModal('RESOLVE', row)}
                disabled={isResolved || !canResolve}
              >
                Resolver
              </Button>,
              !canResolve
            )}
          </Space>
        );
      },
    },
  ];

  const canEvaluate = canExecuteAction('alerts.evaluate');

  return (
    <div style={{ padding: 24 }}>
      <Title level={2} style={{ marginTop: 0 }}>Alert Center</Title>
      <Text type="secondary">
        Alertas internos para operacao do SaaS Billing. Esta tela nao envia notificacoes externas e nao altera assinatura/entitlement.
      </Text>

      {error ? (
        <Alert
          type="error"
          showIcon
          style={{ marginTop: 16 }}
          message="Falha no Alert Center"
          description={error}
        />
      ) : null}

      <Alert
        type="info"
        showIcon
        style={{ marginTop: 16 }}
        message="Escopo interno"
        description="Reconhecer e resolver alertas atua apenas no controle interno da plataforma. Nao ha envio de e-mail, WhatsApp ou webhook externo."
      />

      <Space wrap size={12} style={{ marginTop: 16 }}>
        <Card size="small"><Statistic title="OPEN" value={statusCounts.OPEN} /></Card>
        <Card size="small"><Statistic title="ACKNOWLEDGED" value={statusCounts.ACKNOWLEDGED} /></Card>
        <Card size="small"><Statistic title="RESOLVED" value={statusCounts.RESOLVED} /></Card>
        <Card size="small"><Statistic title="CRITICAL" value={severityCounts.CRITICAL} /></Card>
        <Card size="small"><Statistic title="ERROR" value={severityCounts.ERROR} /></Card>
      </Space>

      <Card size="small" title="Filtros e avaliacao" style={{ marginTop: 16 }}>
        <Space wrap>
          <Input
            placeholder="tenant_id"
            value={filters.tenant_id}
            onChange={(event) => setFilters((prev) => ({ ...prev, tenant_id: event.target.value }))}
            style={{ width: 220 }}
          />
          <Select
            placeholder="status"
            options={STATUS_OPTIONS}
            value={filters.status}
            onChange={(value) => setFilters((prev) => ({ ...prev, status: value }))}
            allowClear
            style={{ width: 180 }}
          />
          <Select
            placeholder="severity"
            options={SEVERITY_OPTIONS}
            value={filters.severity}
            onChange={(value) => setFilters((prev) => ({ ...prev, severity: value }))}
            allowClear
            style={{ width: 180 }}
          />
          <Input
            placeholder="alert_code"
            value={filters.alert_code}
            onChange={(event) => setFilters((prev) => ({ ...prev, alert_code: event.target.value }))}
            style={{ width: 220 }}
          />
          <Input
            placeholder="source"
            value={filters.source}
            onChange={(event) => setFilters((prev) => ({ ...prev, source: event.target.value }))}
            style={{ width: 180 }}
          />
          <DatePicker.RangePicker
            showTime
            value={filters.range}
            onChange={(value) => setFilters((prev) => ({ ...prev, range: value }))}
          />
          <Input
            type="number"
            min={1}
            max={500}
            value={filters.limit}
            onChange={(event) => setFilters((prev) => ({ ...prev, limit: event.target.value }))}
            style={{ width: 120 }}
          />
          <Button type="primary" onClick={fetchAlerts} loading={loading}>Aplicar</Button>
          {withPermissionTooltip(
            <Button onClick={evaluateNow} loading={evaluating} disabled={!canEvaluate}>Avaliar agora</Button>,
            !canEvaluate
          )}
        </Space>
      </Card>

      <Card size="small" title={`Alertas (${items.length})`} style={{ marginTop: 16 }}>
        <Table
          rowKey={(row) => row.id}
          columns={columns}
          dataSource={items}
          loading={loading}
          pagination={{ pageSize: 20, showSizeChanger: false }}
          scroll={{ x: 1300 }}
        />
      </Card>

      <Modal
        title={actionModal.mode === 'ACKNOWLEDGE' ? 'Reconhecer alerta' : 'Resolver alerta'}
        open={actionModal.open}
        confirmLoading={actionLoading}
        onOk={submitAction}
        onCancel={closeActionModal}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text strong>{actionModal.alert?.title || '-'}</Text>
          <Input.TextArea
            rows={4}
            placeholder="Notas internas (opcional)"
            value={actionModal.notes}
            onChange={(event) => setActionModal((prev) => ({ ...prev, notes: event.target.value }))}
          />
        </Space>
      </Modal>
    </div>
  );
};

export default PlatformAlertCenterPage;
